import React,{useCallback,useEffect,useState} from "react";
import {apiFetch} from "../api/client";
import {tenantPath} from "../utils/tenantContext";
import "./AttackReconstructionPanel.css";

async function requestJson(path,options={}){
  const response=await apiFetch(tenantPath(path),options);
  let body={};
  try{body=await response.json();}catch(_){}
  if(!response.ok) throw new Error(body.detail||`Request failed (${response.status})`);
  return body;
}

function AttackReconstructionPanel({captureId}){
  const [analysis,setAnalysis]=useState(null);
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");

  const load=useCallback(async()=>{
    if(!captureId){setAnalysis(null);return;}
    try{
      setError("");
      const data=await requestJson(`/network-protection/pcap/analysis/${encodeURIComponent(captureId)}/latest`);
      setAnalysis(data.available?data.analysis:null);
    }catch(e){setError(e.message);}
  },[captureId]);

  useEffect(()=>{load();},[load]);

  const analyze=async()=>{
    if(!captureId)return;
    try{
      setBusy(true);setError("");
      const data=await requestJson(`/network-protection/pcap/analysis/${encodeURIComponent(captureId)}/reconstruct`,{method:"POST"});
      setAnalysis(data);
    }catch(e){setError(e.message);}
    finally{setBusy(false);}
  };

  if(!captureId)return null;

  const origins=analysis?.origin_summary?.origin_candidates||[];
  const destinations=analysis?.impact_summary?.observed_destinations||[];
  const timeline=Array.isArray(analysis?.attack_timeline)?analysis.attack_timeline:[];
  const breach=analysis?.breach_assessment||{};

  return (
    <section className="attackReconstructionPanel">
      <div className="attackReconstructionHeader">
        <div>
          <span>R21 AUTOMATED FORENSICS</span>
          <h3>Attack Reconstruction</h3>
          <p>Automated origin, destination, service and activity reconstruction.</p>
        </div>
        <button type="button" disabled={busy} onClick={analyze}>
          {busy?"Analyzing...":"Analyze Capture"}
        </button>
      </div>

      {error?<div className="attackReconstructionError">{error}</div>:null}

      {!analysis?(
        <div className="attackReconstructionEmpty">No automated reconstruction has been generated for this capture.</div>
      ):(
        <>
          <div className="attackReconstructionMetrics">
            <div><span>Packets</span><strong>{analysis.packet_count??0}</strong></div>
            <div><span>Flows</span><strong>{analysis.flow_count??0}</strong></div>
            <div><span>Breach Confirmed</span><strong>{breach.breach_confirmed?"YES":"NO"}</strong></div>
            <div><span>Human Attribution</span><strong>{breach.human_attribution||"NOT DETERMINED"}</strong></div>
          </div>

          <div className="attackReconstructionSections">
            <article>
              <h4>Origin</h4>
              {origins.map(o=><div className="attackReconRow" key={o.ip}><strong>{o.ip}</strong><span>{o.packet_count} packets</span></div>)}
            </article>

            <article>
              <h4>Observed Impact Surface</h4>
              {destinations.map(d=><div className="attackReconRow" key={d.ip}><strong>{d.ip}</strong><span>Ports: {(d.destination_ports||[]).join(", ")||"—"}</span></div>)}
            </article>
          </div>

          <div className="attackReconstructionAssessment">
            <h4>Breach Assessment</h4>
            <p>{breach.assessment||"No assessment."}</p>
          </div>

          <div className="attackReconstructionTimeline">
            <h4>Automated Activity Timeline</h4>
            {timeline.length===0?<p>No derived timeline events.</p>:timeline.slice(0,100).map((e,i)=>
              <div className="attackTimelineEvent" key={`${e.type}-${i}`}>
                <strong>{e.type?.replaceAll("_"," ").toUpperCase()}</strong>
                <span>{e.source||"?"} → {e.destination||"?"}</span>
                {e.destination_port?<span>Port {e.destination_port} {e.service?`(${e.service})`:""}</span>:null}
                {e.query?<code>DNS: {e.query}</code>:null}
                {e.server_name?<code>TLS SNI: {e.server_name}</code>:null}
                {e.method?<code>HTTP: {e.method} {e.host||""}{e.target||""}</code>:null}
              </div>
            )}
          </div>

          <div className="attackReconstructionBoundary">
            Packet evidence can establish network origin, destination, protocol and timing. Successful compromise, process execution, file impact and human identity require endpoint evidence.
          </div>
        </>
      )}
    </section>
  );
}

export default AttackReconstructionPanel;
